/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getCredentialStatusDecoder,
  getCredentialStatusEncoder,
  getCrossChainStatusDecoder,
  getCrossChainStatusEncoder,
  type CredentialStatus,
  type CredentialStatusArgs,
  type CrossChainStatus,
  type CrossChainStatusArgs,
} from "../types";

export const CREDENTIAL_DISCRIMINATOR = new Uint8Array([
  145, 44, 68, 220, 67, 46, 100, 135,
]);

export function getCredentialDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(CREDENTIAL_DISCRIMINATOR);
}

export type Credential = {
  discriminator: ReadonlyUint8Array;
  /** Reference to the template used */
  template: Address;
  /** The subject (recipient) of the credential */
  subject: Address;
  /** The issuer who created this credential */
  issuer: Address;
  /** Unique credential ID (urn:uuid:xxx format) */
  credentialId: string;
  /**
   * SHA-256 hash of the credential subject data
   * Actual data stored off-chain (IPFS) to save space
   */
  subjectDataHash: ReadonlyUint8Array;
  /** URI to the full credential subject data (IPFS/HTTPS) */
  subjectDataUri: string;
  /** Current status */
  status: CredentialStatus;
  /**
   * Ed25519 signature of the credential for off-chain verification
   * Signs: sha256(credential_id || subject || subject_data_hash || issued_at)
   */
  signature: ReadonlyUint8Array;
  /** Issuance timestamp */
  issuedAt: bigint;
  /** Expiration timestamp (None = never expires) */
  expiresAt: Option<bigint>;
  /** Revocation timestamp (if revoked) */
  revokedAt: Option<bigint>;
  /** Cross-chain sync status */
  crossChainStatus: CrossChainStatus;
  /** Crossmint credential ID (if synced to EVM) */
  crossmintCredentialId: Option<string>;
  /** Source data reference (e.g., agent PDA, escrow PDA) */
  sourceAccount: Option<Address>;
  /** PDA bump */
  bump: number;
};

export type CredentialArgs = {
  /** Reference to the template used */
  template: Address;
  /** The subject (recipient) of the credential */
  subject: Address;
  /** The issuer who created this credential */
  issuer: Address;
  /** Unique credential ID (urn:uuid:xxx format) */
  credentialId: string;
  /**
   * SHA-256 hash of the credential subject data
   * Actual data stored off-chain (IPFS) to save space
   */
  subjectDataHash: ReadonlyUint8Array;
  /** URI to the full credential subject data (IPFS/HTTPS) */
  subjectDataUri: string;
  /** Current status */
  status: CredentialStatusArgs;
  /**
   * Ed25519 signature of the credential for off-chain verification
   * Signs: sha256(credential_id || subject || subject_data_hash || issued_at)
   */
  signature: ReadonlyUint8Array;
  /** Issuance timestamp */
  issuedAt: number | bigint;
  /** Expiration timestamp (None = never expires) */
  expiresAt: OptionOrNullable<number | bigint>;
  /** Revocation timestamp (if revoked) */
  revokedAt: OptionOrNullable<number | bigint>;
  /** Cross-chain sync status */
  crossChainStatus: CrossChainStatusArgs;
  /** Crossmint credential ID (if synced to EVM) */
  crossmintCredentialId: OptionOrNullable<string>;
  /** Source data reference (e.g., agent PDA, escrow PDA) */
  sourceAccount: OptionOrNullable<Address>;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link CredentialArgs} account data. */
export function getCredentialEncoder(): Encoder<CredentialArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["template", getAddressEncoder()],
      ["subject", getAddressEncoder()],
      ["issuer", getAddressEncoder()],
      ["credentialId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["subjectDataHash", fixEncoderSize(getBytesEncoder(), 32)],
      [
        "subjectDataUri",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["status", getCredentialStatusEncoder()],
      ["signature", fixEncoderSize(getBytesEncoder(), 64)],
      ["issuedAt", getI64Encoder()],
      ["expiresAt", getOptionEncoder(getI64Encoder())],
      ["revokedAt", getOptionEncoder(getI64Encoder())],
      ["crossChainStatus", getCrossChainStatusEncoder()],
      [
        "crossmintCredentialId",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["sourceAccount", getOptionEncoder(getAddressEncoder())],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CREDENTIAL_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link Credential} account data. */
export function getCredentialDecoder(): Decoder<Credential> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["template", getAddressDecoder()],
    ["subject", getAddressDecoder()],
    ["issuer", getAddressDecoder()],
    ["credentialId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["subjectDataHash", fixDecoderSize(getBytesDecoder(), 32)],
    ["subjectDataUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["status", getCredentialStatusDecoder()],
    ["signature", fixDecoderSize(getBytesDecoder(), 64)],
    ["issuedAt", getI64Decoder()],
    ["expiresAt", getOptionDecoder(getI64Decoder())],
    ["revokedAt", getOptionDecoder(getI64Decoder())],
    ["crossChainStatus", getCrossChainStatusDecoder()],
    [
      "crossmintCredentialId",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["sourceAccount", getOptionDecoder(getAddressDecoder())],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link Credential} account data. */
export function getCredentialCodec(): Codec<CredentialArgs, Credential> {
  return combineCodec(getCredentialEncoder(), getCredentialDecoder());
}

export function decodeCredential<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<Credential, TAddress>;
export function decodeCredential<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<Credential, TAddress>;
export function decodeCredential<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<Credential, TAddress> | MaybeAccount<Credential, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getCredentialDecoder(),
  );
}

export async function fetchCredential<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<Credential, TAddress>> {
  const maybeAccount = await fetchMaybeCredential(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeCredential<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<Credential, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeCredential(maybeAccount);
}

export async function fetchAllCredential(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<Credential>[]> {
  const maybeAccounts = await fetchAllMaybeCredential(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeCredential(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<Credential>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeCredential(maybeAccount));
}
